import { useCallback, useRef, useState } from 'react';

/**
 * An element's content box, kept current by a ResizeObserver. Returns a callback ref so it works
 * on elements that mount late (a details panel that appears when its card expands).
 */
export function useElementSize<T extends Element = HTMLDivElement>() {
  const [size, setSize] = useState({ width: 0, height: 0 });
  const obs = useRef<ResizeObserver | null>(null);

  const ref = useCallback((node: T | null) => {
    obs.current?.disconnect();
    obs.current = null;
    if (!node || typeof ResizeObserver === 'undefined') return;
    const o = new ResizeObserver(([entry]) => {
      const { width, height } = entry.contentRect;
      // Rounded, so sub-pixel jitter during a layout does not re-render every card on the board.
      setSize((prev) =>
        Math.round(prev.width) === Math.round(width) && Math.round(prev.height) === Math.round(height)
          ? prev
          : { width, height },
      );
    });
    o.observe(node);
    obs.current = o;
  }, []);

  return [ref, size] as const;
}
